import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {
  CalendarX,
  Check,
  LogIn,
  LogOut,
  ArrowRight,
  Lock,
  X,
  SkipForward,
  Save,
  Info,
  AlertTriangle,
  Loader2,
  Bed,
  Trash2,
  CircleDot,
} from "lucide-react";
import "./CalendarModal.css";

dayjs.extend(isBetween);

/* ─── Custom Mini-Month Grid ───────────────────────────────────────────────── */
const MiniMonthGrid = ({
  month,
  year,
  bookingStart,
  bookingEnd,
  selectedOffDays,
  existingOffDays,
  onToggle,
  allExistingOffDaysMap,
  selectedRoomId,
  normalizeDate,
}) => {
  const firstDay = dayjs(`${year}-${String(month + 1).padStart(2, "0")}-01`);
  const daysInMonth = firstDay.daysInMonth();
  const startDow = firstDay.day();

  // Build flat array of cells (nulls for leading blanks)
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(
      dayjs(
        `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      ),
    );
  }

  const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <div className="mini-month">
      <div className="mini-month__title">{firstDay.format("MMM YYYY")}</div>
      <div className="mini-month__grid">
        {dayLabels.map((l, i) => (
          <div key={`lbl-${i}`} className="mini-month__day-label">
            {l}
          </div>
        ))}
        {cells.map((date, idx) => {
          if (!date)
            return (
              <div
                key={`e-${idx}`}
                className="mini-month__cell mini-month__cell--empty"
              />
            );

          const dateStr = normalizeDate(date);
          const isInRange = date.isBetween(
            bookingStart,
            bookingEnd,
            "day",
            "[)",
          );
          const isNewOff = selectedOffDays.has(dateStr);
          const isExistingOff = existingOffDays.has(dateStr);
          const isToday = date.isSame(dayjs(), "day");

          let otherRoomOff = false;
          if (selectedRoomId !== "all") {
            allExistingOffDaysMap.forEach((roomDays, roomId) => {
              if (roomId !== selectedRoomId && roomDays.has(dateStr))
                otherRoomOff = true;
            });
          }

          let cls = "mini-month__cell";
          if (!isInRange) cls += " mini-month__cell--disabled";
          else if (isExistingOff) cls += " mini-month__cell--existing";
          else if (isNewOff) cls += " mini-month__cell--off";
          else cls += " mini-month__cell--available";
          if (isToday && isInRange) cls += " mini-month__cell--today";
          if (otherRoomOff && !isExistingOff && !isNewOff && isInRange)
            cls += " mini-month__cell--other-room";

          return (
            <div
              key={dateStr}
              className={cls}
              onClick={() => isInRange && !isExistingOff && onToggle(date)}
              title={
                isExistingOff
                  ? "Existing off-day (locked)"
                  : isNewOff
                    ? "Click to remove off-day"
                    : isInRange
                      ? "Click to mark as off-day"
                      : "Outside booking"
              }
            >
              {date.date()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Main CalendarModal Component ─────────────────────────────────────────── */
const CalendarModal = ({
  show,
  onHide,
  onConfirm,
  statusData,
  title = "Mark Off Days",
  bookingStartDate,
  bookingEndDate,
  initialOffDays = [],
  existingOffDaysData = null,
  reservationData = null,
  showPricing = true,
  isProcessing = false,
  processingMessage = "Processing off-days...",
  showCompletionOptions = false,
  onProceedToCheckout = null,
  onCompleteClose = null,
  pendingStatusDescription = "",
  isUpdatingStatus = false,
}) => {
  const normalizeDate = useCallback(
    (date) => dayjs(date).format("YYYY-MM-DD"),
    [],
  );

  const bookingStart = dayjs(bookingStartDate);
  const bookingEnd = dayjs(bookingEndDate);
  const totalBookingNights = bookingEnd.diff(bookingStart, "day");
  const totalBookingDays = totalBookingNights + 1;

  /* ── State ── */
  const [roomOffDaysMap, setRoomOffDaysMap] = useState(new Map());
  const [existingOffDaysSet, setExistingOffDaysSet] = useState(new Set());
  const [allExistingOffDaysMap, setAllExistingOffDaysMap] = useState(new Map());
  const [validationError, setValidationError] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState("all");

  const currentRoomOffDays = useMemo(() => {
    if (selectedRoomId === "all") {
      const all = new Set();
      roomOffDaysMap.forEach((rds) => rds.forEach((d) => all.add(d)));
      return all;
    }
    return roomOffDaysMap.get(selectedRoomId) || new Set();
  }, [roomOffDaysMap, selectedRoomId]);

  /* ── Pricing calculation ── */
  const calculatePricingInfo = useCallback(() => {
    if (!reservationData || !showPricing)
      return { originalTotalPrice: 0, dailyRate: 0, hasPricing: false };
    const reservation = Array.isArray(reservationData)
      ? reservationData[0]
      : reservationData;
    if (selectedRoomId !== "all") {
      const roomService = reservation?.services?.find(
        (s) => s.roomId?.toString() === selectedRoomId.toString(),
      );
      if (roomService) {
        const roomTotal =
          roomService.service_detail_total ||
          roomService.servicePrice * totalBookingNights ||
          0;
        return {
          originalTotalPrice: roomTotal,
          dailyRate:
            totalBookingNights > 0 ? roomTotal / totalBookingNights : 0,
          hasPricing: true,
          isRoomSpecific: true,
          roomId: selectedRoomId,
        };
      }
    }
    const originalTotal = reservation?.subTotal || 0;
    return {
      originalTotalPrice: originalTotal,
      dailyRate:
        totalBookingNights > 0 ? originalTotal / totalBookingNights : 0,
      hasPricing: true,
      isRoomSpecific: false,
    };
  }, [reservationData, showPricing, totalBookingNights, selectedRoomId]);

  const pricingInfo = calculatePricingInfo();

  /* ── Available rooms ── */
  const availableRooms = useMemo(() => {
    if (!reservationData) return [];
    const reservation = Array.isArray(reservationData)
      ? reservationData[0]
      : reservationData;
    const services = reservation?.services || [];
    const roomsMap = new Map();
    let guestCounter = 1;
    services.forEach((service) => {
      const roomId = service.roomId;
      let subCustomerName = service.subCustomerName;
      if (!subCustomerName || subCustomerName.trim() === "") {
        const isCompany =
          reservation.companyID > 0 ||
          (reservation.companyName && reservation.companyName.trim() !== "");
        subCustomerName = isCompany
          ? `Guest ${guestCounter}`
          : reservation.customerName || `Guest ${guestCounter}`;
        guestCounter++;
      }
      if (roomId && !roomsMap.has(roomId)) {
        roomsMap.set(roomId, {
          roomId,
          roomNo: service.roomNo || `Room ${roomId}`,
          subCustomerName,
          serviceType: service.service_type_name || service.productServiceName,
          servicePrice: service.servicePrice || 0,
          subCustomerId: service.subCustomerId,
          checkIn: service.subCustomerCheckIn || reservation.orderDate,
          checkOut: service.subCustomerCheckOut || reservation.checkOutDate,
          serviceId: service.serviceId,
          serviceTypeId: service.serviceTypeId,
          service_detail_total: service.service_detail_total || 0,
        });
      }
    });
    return Array.from(roomsMap.values());
  }, [reservationData]);

  /* ── Pricing state ── */
  const [currentTotalPrice, setCurrentTotalPrice] = useState(
    pricingInfo.originalTotalPrice,
  );
  const [priceReduction, setPriceReduction] = useState(0);

  /* ── Initialize existing off-days ── */
  useEffect(() => {
    if (existingOffDaysData?.data?.offDays) {
      const byRoom = new Map();
      existingOffDaysData.data.offDays.forEach((od) => {
        const rid = od.roomId?.toString();
        const ds = normalizeDate(od.offDate);
        if (!byRoom.has(rid)) byRoom.set(rid, new Set());
        byRoom.get(rid).add(ds);
      });
      setAllExistingOffDaysMap(byRoom);
      if (selectedRoomId !== "all") {
        setExistingOffDaysSet(byRoom.get(selectedRoomId) || new Set());
      } else {
        const all = new Set();
        byRoom.forEach((s) => s.forEach((d) => all.add(d)));
        setExistingOffDaysSet(all);
      }
    } else {
      setAllExistingOffDaysMap(new Map());
      setExistingOffDaysSet(new Set());
    }
  }, [existingOffDaysData, normalizeDate, selectedRoomId]);

  useEffect(() => {
    if (selectedRoomId === "all") {
      const all = new Set();
      allExistingOffDaysMap.forEach((s) => s.forEach((d) => all.add(d)));
      setExistingOffDaysSet(all);
    } else {
      setExistingOffDaysSet(
        allExistingOffDaysMap.get(selectedRoomId) || new Set(),
      );
    }
  }, [selectedRoomId, allExistingOffDaysMap]);

  useEffect(() => {
    if (initialOffDays.length > 0) {
      setRoomOffDaysMap(
        new Map([["all", new Set(initialOffDays.map(normalizeDate))]]),
      );
    } else {
      setRoomOffDaysMap(new Map());
    }
  }, [initialOffDays, normalizeDate]);

  useEffect(() => {
    if (pricingInfo.hasPricing) {
      const red = currentRoomOffDays.size * pricingInfo.dailyRate;
      setPriceReduction(red);
      setCurrentTotalPrice(Math.max(0, pricingInfo.originalTotalPrice - red));
    }
  }, [currentRoomOffDays.size, pricingInfo]);

  /* ── Helpers ── */
  const isWithinBookingPeriod = useCallback(
    (date) => dayjs(date).isBetween(bookingStart, bookingEnd, "day", "[)"),
    [bookingStart, bookingEnd],
  );

  const toggleOffDay = useCallback(
    (date) => {
      const dateStr = normalizeDate(date);
      if (!isWithinBookingPeriod(date) || existingOffDaysSet.has(dateStr))
        return;
      setRoomOffDaysMap((prev) => {
        const nm = new Map(prev);
        const rid = selectedRoomId === "all" ? "all" : selectedRoomId;
        const updated = new Set(nm.get(rid) || new Set());
        updated.has(dateStr) ? updated.delete(dateStr) : updated.add(dateStr);
        nm.set(rid, updated);
        return nm;
      });
      setValidationError("");
    },
    [normalizeDate, isWithinBookingPeriod, selectedRoomId, existingOffDaysSet],
  );

  /* ── Months to render (include checkout month so users always see it) ── */
  const monthsToRender = useMemo(() => {
    const months = [];
    let cursor = bookingStart.startOf("month");
    const end = bookingEnd.startOf("month"); // include checkout month
    while (cursor.isBefore(end, "month") || cursor.isSame(end, "month")) {
      months.push({ month: cursor.month(), year: cursor.year() });
      cursor = cursor.add(1, "month");
    }
    return months;
  }, [bookingStart, bookingEnd]);

  /* ── Validation ── */
  const validateOffDays = useCallback(() => {
    const newCount = currentRoomOffDays.size;
    const totalOff = existingOffDaysSet.size + newCount;
    const used = totalBookingDays - totalOff;
    if (newCount === 0) {
      setValidationError("Please select at least one new off-day.");
      return false;
    }
    if (used === 0) {
      setValidationError(
        "Cannot mark all days as off. At least one day must remain.",
      );
      return false;
    }
    if (selectedRoomId === "all") {
      for (const [rid, rods] of roomOffDaysMap) {
        if (rid !== "all" && rods.size === totalBookingDays) {
          const room = availableRooms.find(
            (r) => r.roomId.toString() === rid.toString(),
          );
          setValidationError(
            `${room ? `${room.roomNo} (${room.subCustomerName})` : `Room ${rid}`} has all days marked off.`,
          );
          return false;
        }
      }
    }
    if (pricingInfo.hasPricing && currentTotalPrice < 0) {
      setValidationError("Total cannot be negative.");
      return false;
    }
    setValidationError("");
    return true;
  }, [
    currentRoomOffDays.size,
    existingOffDaysSet.size,
    totalBookingDays,
    pricingInfo,
    currentTotalPrice,
    selectedRoomId,
    roomOffDaysMap,
    availableRooms,
  ]);

  /* ── Handle save ── */
  const handleSave = useCallback(() => {
    if (!validateOffDays()) return;

    const allRoomsOffDays = new Map();
    const consolidatedOffDays = new Set();
    roomOffDaysMap.forEach((rods, rid) => {
      if (rid !== "all" && rods.size > 0) {
        allRoomsOffDays.set(rid, Array.from(rods));
        rods.forEach((d) => consolidatedOffDays.add(d));
      }
    });
    if (allRoomsOffDays.size === 0 && roomOffDaysMap.has("all")) {
      const a = Array.from(roomOffDaysMap.get("all"));
      allRoomsOffDays.set("all", a);
      a.forEach((d) => consolidatedOffDays.add(d));
    }

    const allForAPI = new Set([...existingOffDaysSet, ...consolidatedOffDays]);
    const newArr = Array.from(consolidatedOffDays);
    const combinedArr = Array.from(allForAPI);

    const extractedRoomId = (() => {
      if (selectedRoomId !== "all") return parseInt(selectedRoomId);
      const svcs = reservationData?.services || [];
      if (svcs.length === 1) return svcs[0]?.roomId || null;
      if (svcs.length > 1) return null;
      return reservationData?.roomId || null;
    })();

    // Log updated reservation if pricing available
    if (reservationData && pricingInfo.hasPricing) {
      const orig = Array.isArray(reservationData)
        ? reservationData[0]
        : reservationData;
      let totalRed = 0;
      if (selectedRoomId !== "all" && pricingInfo.isRoomSpecific) {
        totalRed =
          (roomOffDaysMap.get(selectedRoomId) || new Set()).size *
          pricingInfo.dailyRate;
      } else {
        roomOffDaysMap.forEach((rods, rid) => {
          if (rid !== "all") {
            const room = availableRooms.find(
              (r) => r.roomId.toString() === rid.toString(),
            );
            if (room)
              totalRed +=
                rods.size * (room.service_detail_total / totalBookingNights);
          }
        });
        if (totalRed === 0 && newArr.length > 0)
          totalRed = newArr.length * (orig.subTotal / totalBookingNights);
      }
      console.log("🔄 Off-days applied:", {
        original: orig.subTotal?.toFixed(2),
        reduction: totalRed.toFixed(2),
        offDays: newArr.length,
      });
    }

    const resultData = {
      offDays: newArr,
      roomSpecificOffDays: Object.fromEntries(allRoomsOffDays),
      statusData,
      summary: {
        totalNights: totalBookingNights,
        totalDays: totalBookingDays,
        usedNights: totalBookingNights - newArr.length,
        usedDays: totalBookingDays - newArr.length,
        offDays: newArr.length,
        roomsAffected: allRoomsOffDays.size,
      },
      roomSelection: {
        selectedRoomId,
        isRoomSpecific: selectedRoomId !== "all",
        roomDetails:
          selectedRoomId !== "all"
            ? availableRooms.find(
                (r) => r.roomId.toString() === selectedRoomId.toString(),
              )
            : null,
      },
      pricing: pricingInfo.hasPricing
        ? {
            originalTotalPrice: pricingInfo.originalTotalPrice,
            updatedTotalPrice: currentTotalPrice,
            priceReduction,
            dailyRate: pricingInfo.dailyRate,
            offDaysCount: currentRoomOffDays.size,
            isRoomSpecific: pricingInfo.isRoomSpecific,
            affectedRoomId: pricingInfo.isRoomSpecific ? selectedRoomId : null,
          }
        : null,
      apiPayload: (() => {
        const base = {
          orderId:
            reservationData?.orderId ||
            reservationData?.orderID ||
            statusData?.orderId,
          offDays: combinedArr,
          bookingStartDate: bookingStart.format("YYYY-MM-DD"),
          bookingEndDate: bookingEnd.format("YYYY-MM-DD"),
          userId: statusData?.userId || null,
          originalAmount: pricingInfo.originalTotalPrice,
        };
        const svcs = reservationData?.services || [];
        if (svcs.length > 1 && selectedRoomId === "all") {
          return svcs.map((s, i) => ({
            ...base,
            roomId: s.roomId,
            notes: `Off-days: ${existingOffDaysSet.size} existing + ${newArr.length} new = ${combinedArr.length} total (Room: ${s.roomId})`,
            roomMetadata: {
              roomNo: s.roomNo || `Room ${s.roomId}`,
              guestName: s.subCustomerName || `Guest ${i + 1}`,
              serviceId: s.serviceId,
              serviceType: s.service_type_name || s.productServiceName,
              isPartOfMultiRoomSelection: true,
              totalRoomsInSelection: svcs.length,
            },
          }));
        }
        return {
          ...base,
          roomId: extractedRoomId,
          notes: `Off-days: ${existingOffDaysSet.size} existing + ${newArr.length} new = ${combinedArr.length} total`,
        };
      })(),
      validation: {
        isValid: newArr.length > 0,
        errors: newArr.length === 0 ? ["Select at least one off-day"] : [],
        warnings:
          combinedArr.length >= totalBookingNights
            ? ["Off-days equal or exceed booking nights"]
            : [],
        existingOffDaysCount: existingOffDaysSet.size,
        newOffDaysCount: newArr.length,
        totalOffDaysCount: combinedArr.length,
      },
      metadata: {
        processedAt: new Date().toISOString(),
        componentVersion: "CalendarModal v3.0",
        processingMode: selectedRoomId === "all" ? "global" : "room-specific",
        dataSource: "calendar-selection",
      },
    };
    onConfirm(resultData);
  }, [
    roomOffDaysMap,
    existingOffDaysSet,
    validateOffDays,
    onConfirm,
    statusData,
    totalBookingDays,
    totalBookingNights,
    pricingInfo,
    currentTotalPrice,
    priceReduction,
    bookingStart,
    bookingEnd,
    reservationData,
    selectedRoomId,
    availableRooms,
    currentRoomOffDays,
  ]);

  /* ── Handle skip ── */
  const handleSkipOffDays = useCallback(() => {
    const skipData = {
      offDays: [],
      roomSpecificOffDays: {},
      statusData,
      summary: {
        totalNights: totalBookingNights,
        totalDays: totalBookingDays,
        usedNights: totalBookingNights,
        usedDays: totalBookingDays,
        offDays: 0,
        roomsAffected: 0,
      },
      roomSelection: {
        selectedRoomId,
        isRoomSpecific: false,
        roomDetails: null,
      },
      pricing: pricingInfo.hasPricing
        ? {
            originalTotalPrice: pricingInfo.originalTotalPrice,
            updatedTotalPrice: pricingInfo.originalTotalPrice,
            priceReduction: 0,
            dailyRate: pricingInfo.dailyRate,
            offDaysCount: 0,
            isRoomSpecific: false,
            affectedRoomId: null,
          }
        : null,
      apiPayload: {
        orderId:
          reservationData?.orderId ||
          reservationData?.orderID ||
          statusData?.orderId,
        offDays: Array.from(existingOffDaysSet),
        roomId: (() => {
          const s = reservationData?.services || [];
          return s.length === 1
            ? s[0]?.roomId || null
            : s.length > 1
              ? null
              : reservationData?.roomId || null;
        })(),
        bookingStartDate: bookingStart.format("YYYY-MM-DD"),
        bookingEndDate: bookingEnd.format("YYYY-MM-DD"),
        userId: statusData?.userId || null,
        originalAmount: pricingInfo.originalTotalPrice,
        notes: `Off-days skipped - ${existingOffDaysSet.size} existing only`,
      },
      validation: {
        isValid: true,
        errors: [],
        warnings:
          existingOffDaysSet.size > 0
            ? [`${existingOffDaysSet.size} existing off-days only`]
            : ["No off-days"],
        existingOffDaysCount: existingOffDaysSet.size,
        newOffDaysCount: 0,
        totalOffDaysCount: existingOffDaysSet.size,
        skipped: true,
      },
      metadata: {
        processedAt: new Date().toISOString(),
        componentVersion: "CalendarModal v3.0",
        processingMode: "skip",
        dataSource: "skip-selection",
      },
      skippedOffDays: true,
    };
    onConfirm(skipData);
  }, [
    statusData,
    totalBookingNights,
    totalBookingDays,
    selectedRoomId,
    pricingInfo,
    reservationData,
    bookingStart,
    bookingEnd,
    existingOffDaysSet,
    onConfirm,
  ]);

  /* ── Handle cancel ── */
  const handleCancel = useCallback(() => {
    if (initialOffDays.length > 0) {
      setRoomOffDaysMap(
        new Map([["all", new Set(initialOffDays.map(normalizeDate))]]),
      );
    } else {
      setRoomOffDaysMap(new Map());
    }
    setValidationError("");
    onHide();
  }, [initialOffDays, normalizeDate, onHide]);

  /* ── Clear current room ── */
  const clearCurrentRoomOffDays = useCallback(() => {
    setRoomOffDaysMap((prev) => {
      const nm = new Map(prev);
      nm.set(selectedRoomId === "all" ? "all" : selectedRoomId, new Set());
      return nm;
    });
    setValidationError("");
  }, [selectedRoomId]);

  /* ── Render ── */
  return (
    <Modal
      show={show}
      onHide={handleCancel}
      size="lg"
      centered
      className="off-days-modal-v2"
      backdrop="static"
    >
      <Modal.Header closeButton className="cm-header">
        <Modal.Title className="cm-title">
          <CalendarX size={18} className="me-2" />
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="cm-body" style={{ position: "relative" }}>
        {/* Loading Overlay */}
        {isProcessing && (
          <div className="cm-overlay">
            <div
              className="spinner-border text-primary mb-2"
              role="status"
              style={{ width: "2.5rem", height: "2.5rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="fw-600 text-primary">{processingMessage}</div>
            <small className="text-muted">Please wait...</small>
          </div>
        )}

        {/* ── Completion Screen ── */}
        {showCompletionOptions ? (
          <div className="cm-completion">
            <div className="text-center mb-3">
              <div className="cm-success-icon mx-auto mb-2">
                <Check size={24} />
              </div>
              <h5 className="text-success mb-1">Off-Days Processed!</h5>
              <p className="text-muted small mb-0">
                What would you like to do next?
              </p>
            </div>
            <div className="row g-2">
              <div className="col-6">
                <button
                  className="btn btn-warning w-100 py-2"
                  onClick={onProceedToCheckout}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? (
                    <>
                      <Loader2 size={14} className="me-1 spin-icon" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <LogOut size={14} className="me-1" />
                      Checkout
                    </>
                  )}
                </button>
              </div>
              <div className="col-6">
                <button
                  className="btn btn-outline-secondary w-100 py-2"
                  onClick={onCompleteClose}
                  disabled={isUpdatingStatus}
                >
                  <Save size={14} className="me-1" />
                  Save & Close
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* ── Booking Period Bar ── */}
            <div className="cm-period-bar">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                <div className="d-flex align-items-center gap-2">
                  <span className="cm-period-item">
                    <LogIn size={13} className="me-1 text-success" />
                    <strong>{bookingStart.format("DD MMM YY")}</strong>
                  </span>
                  <ArrowRight size={12} className="text-muted" />
                  <span className="cm-period-item">
                    <LogOut size={13} className="me-1 text-danger" />
                    <strong>{bookingEnd.format("DD MMM YY")}</strong>
                  </span>
                </div>
                <span className="badge bg-primary px-2 py-1">
                  {totalBookingNights} night
                  {totalBookingNights !== 1 ? "s" : ""}
                </span>
              </div>
            </div>

            {/* ── Room Selector ── */}
            {availableRooms.length > 1 && (
              <div className="cm-room-selector">
                <select
                  className="form-select form-select-sm"
                  value={selectedRoomId}
                  onChange={(e) => setSelectedRoomId(e.target.value)}
                >
                  <option value="all">
                    All Rooms ({availableRooms.length})
                  </option>
                  {availableRooms.map((room) => (
                    <option key={room.roomId} value={room.roomId}>
                      {room.roomNo || `Room ${room.roomId}`} —{" "}
                      {room.subCustomerName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* ── Main Layout ── */}
            <div className="row g-3">
              {/* Calendar */}
              <div className="col-lg-8">
                <div className="cm-calendar-wrap">
                  <div className="cm-legend-row">
                    <span>
                      <span className="cm-dot cm-dot--available"></span>
                      Available
                    </span>
                    <span>
                      <span className="cm-dot cm-dot--off"></span>Off Day
                    </span>
                    {existingOffDaysSet.size > 0 && (
                      <span>
                        <span className="cm-dot cm-dot--existing"></span>
                        Existing
                      </span>
                    )}
                    <span>
                      <span className="cm-dot cm-dot--disabled"></span>Outside
                    </span>
                  </div>

                  <div
                    className={`cm-months-grid cm-months-grid--${Math.min(monthsToRender.length, 3)}`}
                  >
                    {monthsToRender.map(({ month, year }) => (
                      <MiniMonthGrid
                        key={`${year}-${month}`}
                        month={month}
                        year={year}
                        bookingStart={bookingStart}
                        bookingEnd={bookingEnd}
                        selectedOffDays={currentRoomOffDays}
                        existingOffDays={existingOffDaysSet}
                        onToggle={toggleOffDay}
                        allExistingOffDaysMap={allExistingOffDaysMap}
                        selectedRoomId={selectedRoomId}
                        normalizeDate={normalizeDate}
                      />
                    ))}
                  </div>

                  <div className="cm-tip">
                    <Info size={12} className="me-1" />
                    Click dates to toggle off-days. Checkout date excluded.
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                {/* Stats */}
                <div className="cm-stats-card">
                  <div className="cm-stat">
                    <div className="cm-stat__value text-success">
                      {totalBookingNights - currentRoomOffDays.size}
                    </div>
                    <div className="cm-stat__label">Used</div>
                  </div>
                  <div className="cm-stat">
                    <div className="cm-stat__value text-danger">
                      {currentRoomOffDays.size}
                    </div>
                    <div className="cm-stat__label">Off</div>
                  </div>
                  <div className="cm-stat">
                    <div
                      className="cm-stat__value"
                      style={{ color: "#b99028" }}
                    >
                      {totalBookingNights}
                    </div>
                    <div className="cm-stat__label">Total</div>
                  </div>
                </div>

                {/* Pricing */}
                {pricingInfo.hasPricing && (
                  <div className="cm-pricing-card">
                    <div className="cm-pricing-row">
                      <span>Nightly Rate</span>
                      <span className="fw-600">
                        R {pricingInfo.dailyRate.toFixed(2)}
                      </span>
                    </div>
                    <div className="cm-pricing-row">
                      <span>
                        {pricingInfo.isRoomSpecific ? "Room Total" : "Original"}
                      </span>
                      <span className="fw-600">
                        R {pricingInfo.originalTotalPrice.toFixed(2)}
                      </span>
                    </div>
                    {currentRoomOffDays.size > 0 && (
                      <div className="cm-pricing-row text-danger">
                        <span>Reduction</span>
                        <span className="fw-600">
                          - R {priceReduction.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="cm-pricing-row cm-pricing-row--total">
                      <span>Updated</span>
                      <span className="fw-700" style={{ color: "#b99028" }}>
                        R {currentTotalPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Selected off-days */}
                {currentRoomOffDays.size > 0 && (
                  <div className="cm-offdays-list">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small fw-600">Selected Off-Days</span>
                      <button
                        className="btn btn-link btn-sm text-danger p-0"
                        onClick={clearCurrentRoomOffDays}
                        style={{ fontSize: "0.7rem" }}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="cm-offdays-chips">
                      {Array.from(currentRoomOffDays)
                        .sort()
                        .map((d) => (
                          <span
                            key={d}
                            className="cm-chip"
                            onClick={() => toggleOffDay(dayjs(d))}
                          >
                            {dayjs(d).format("DD MMM")}
                            <X size={10} className="ms-1" />
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {/* Existing off-days */}
                {existingOffDaysSet.size > 0 && (
                  <div className="cm-existing-list">
                    <span className="small fw-600 text-danger">
                      <Lock size={11} className="me-1" />
                      Existing ({existingOffDaysSet.size})
                    </span>
                    <div className="cm-existing-chips mt-1">
                      {Array.from(existingOffDaysSet)
                        .sort()
                        .map((d) => (
                          <span key={d} className="cm-chip cm-chip--existing">
                            {dayjs(d).format("DD MMM")}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {validationError && (
                  <div className="alert alert-danger py-2 px-3 mt-2 mb-0 small">
                    <AlertTriangle size={12} className="me-1" />
                    {validationError}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      {/* ── Footer ── */}
      {!showCompletionOptions && (
        <Modal.Footer className="cm-footer">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            <X size={14} className="me-1" />
            Cancel
          </button>
          <button
            className="btn btn-sm btn-warning"
            onClick={handleSkipOffDays}
            disabled={isProcessing}
          >
            <SkipForward size={14} className="me-1" />
            Skip
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={
              !!validationError || isProcessing || currentRoomOffDays.size === 0
            }
          >
            {isProcessing ? (
              <>
                <Loader2 size={14} className="me-1 spin-icon" />
                Processing...
              </>
            ) : (
              <>
                <Save size={14} className="me-1" />
                Save ({currentRoomOffDays.size})
              </>
            )}
          </button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default CalendarModal;
